import L, { LatLngBounds, LatLngBoundsLiteral, LeafletMouseEvent, Map, PolylineOptions } from 'leaflet';
import { toJS } from 'mobx';
import { ISegmentation } from '../../annotations.interface';

interface ISegmentationFrameDrawerOptions {
  readonly: boolean;
  editable: boolean;
  segmentation?: ISegmentation;
  selectSegmentation(id: string): void;
  segmentationDeselected(): Promise<void>;
  nextSegmentationSelected(): ISegmentation | undefined;
  previousSegmentationSelected(): ISegmentation | undefined;
}

export class SegmentationFrameDrawer {
  private _map: Map;
  private _polygonFrame: L.Rectangle | undefined;
  private _options: ISegmentationFrameDrawerOptions;
  private readonly FRAME_PADDING: number = 5;
  private readonly FRAME_PADDING_POINT: number = 10;

  constructor(map: Map, options: ISegmentationFrameDrawerOptions) {
    this._map = map;
    this._options = options;
  }

  public update(options: ISegmentationFrameDrawerOptions): void {
    this._options = options;

    this.updateFrame();
  }

  public addHandlers(): void {
    document.addEventListener('keydown', this.handleKeyboardDownEvents);

    this._map.on('click', this.mapClicked);
  }

  public removeHandlers(): void {
    this.clearFrame();
    document.removeEventListener('keydown', this.handleKeyboardDownEvents);

    this._map.off('click', this.mapClicked);
  }

  mapClicked = (e: LeafletMouseEvent): void => {
    const target = e.originalEvent.target as HTMLDivElement;
    if (this._options.readonly && target.classList.contains('leaflet-container')) {
      void this._options.segmentationDeselected();
    }
  };

  updateFrame(): void {
    if (this._polygonFrame) {
      this._polygonFrame.removeFrom(this._map);
    }
    if (!this._options.segmentation) {
      return;
    }

    const frameOptions: PolylineOptions = {
      color: this._options.segmentation.feature.color,
      weight: 2,
      dashArray: '5, 5',
      opacity: 0.7,
      fill: false,
    };

    this._polygonFrame = L.rectangle(this.getBounds(toJS(this._options.segmentation)), frameOptions);
    this._polygonFrame.addTo(this._map);
  }

  clearFrame(): void {
    if (this._polygonFrame) {
      this._polygonFrame.removeFrom(this._map);
    }
  }

  getBounds(segmentation: ISegmentation): LatLngBounds {
    const bounds = L.geoJSON(segmentation.feature.geometry).getBounds();
    const framePadding = segmentation.feature.geometry.type === 'Point' ? this.FRAME_PADDING_POINT : this.FRAME_PADDING;

    const extended: LatLngBoundsLiteral = [
      [bounds.getSouth() - framePadding, bounds.getWest() - framePadding],
      [bounds.getNorth() + framePadding, bounds.getEast() + framePadding],
    ];

    return new LatLngBounds(extended);
  }

  centerOnSegmentation(segmentation: ISegmentation | undefined): void {
    if (!segmentation) {
      return;
    }
    const bounds = this.getBounds(toJS(segmentation));
    this._map.panTo(bounds.getCenter(), { animate: false });
  }

  handleKeyboardDownEvents = (e: KeyboardEvent): void => {
    switch (e.key) {
      case 'Escape':
        if (this._options.segmentation) {
          void this._options.segmentationDeselected();
        }
        break;
    }
  };
}
